import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { HouseService } from '../../../core/services';
import { HOUSE_FLOOR_MAX, HOUSE_FLOOR_MIN } from '../../../core/constants/app-constants';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

export interface HouseSaveEvent {
  mode: 'create' | 'edit';
  refId?: number | null;
  houseName: string;
}

@Component({
  selector: 'app-new-edit-house',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './new-edit-house.component.html',
  styleUrls: [
    './new-edit-house.component.css',
    './../../../../CSSFiles/BTN_Style.css'
  ]
})
export class NewEditHouseComponent implements OnChanges {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialHouse: any | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<HouseSaveEvent>();

  house = {
    name: '',
    address: ''
  };

  readonly floorMin = HOUSE_FLOOR_MIN;
  readonly floorMax = HOUSE_FLOOR_MAX;
  floorCount = HOUSE_FLOOR_MIN;
  floors = this.buildFloors(this.floorCount);
  isSubmitting = false;
  isSubmitted = false;
  error: string | null = null;

  constructor(private readonly houses: HouseService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialHouse'] || changes['mode']) {
      this.applyInitialValues();
    }
  }

  updateFloors(): void {
    this.floorCount = this.normalizeFloorCount(this.floorCount);
    this.floors = this.buildFloors(this.floorCount);
  }

  increaseFloorCount(): void {
    this.floorCount = this.normalizeFloorCount(this.floorCount + 1);
    this.updateFloors();
  }

  decreaseFloorCount(): void {
    this.floorCount = this.normalizeFloorCount(this.floorCount - 1);
    this.updateFloors();
  }

  closeModal(): void {
    this.close.emit();
  }

  submitForm(): void {
    this.isSubmitted = true;
    this.error = null;

    if (!this.house.name.trim()) {
      this.error = 'House name is required.';
      return;
    }

    if (!this.house.address.trim()) {
      this.error = 'House address is required.';
      return;
    }

    const floorNames = this.floors.map((floor) => floor.name.trim());
    const missingFloorIndex = floorNames.findIndex((name) => name.length === 0);
    if (missingFloorIndex !== -1) {
      this.error = `Floor ${missingFloorIndex + 1} name is required.`;
      return;
    }

    const payload: any = {
      houseName: this.house.name.trim(),
      houseNo: this.initialHouse?.houseNo?.toString().trim() || this.house.name.trim(),
      houseAddress: this.house.address.trim(),
      floors: floorNames
    };

    if (this.mode === 'edit') {
      payload.id = this.initialHouse?.id;
    }

    this.isSubmitting = true;

    const request$ = this.mode === 'edit'
      ? this.houses.update(payload)
      : this.houses.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          const refId = Number(res?.refId ?? res?.data?.id ?? null);
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : null,
            houseName: this.house.name.trim()
          });
        },
        error: (err: any) => {
          this.error = err.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} house.`;
        }
      });
  }

  private buildFloors(count: number): Array<{ index: number; name: string }> {
    return Array.from({ length: count }, (_, index) => ({
      index: index + 1,
      name: ''
    }));
  }

  private applyInitialValues(): void {
    if (this.mode !== 'edit' || !this.initialHouse) {
      this.house = { name: '', address: '' };
      this.floorCount = this.floorMin;
      this.updateFloors();
      return;
    }

    this.house = {
      name: (this.initialHouse.houseName || this.initialHouse.houseNo || '').toString(),
      address: (this.initialHouse.houseAddress || '').toString()
    };

    const apiFloors = Array.isArray(this.initialHouse.floors)
      ? this.initialHouse.floors.map((item: unknown) => `${item ?? ''}`.trim()).filter((name: string) => name.length > 0)
      : [];

    const totalFloor = Number(this.initialHouse.totalFloor);
    if (apiFloors.length > 0) {
      this.floorCount = this.normalizeFloorCount(apiFloors.length);
      this.floors = apiFloors
        .slice(0, this.floorCount)
        .map((name: string, index: number) => ({ index: index + 1, name }));
      return;
    }

    if (Number.isInteger(totalFloor) && totalFloor > 0) {
      this.floorCount = this.normalizeFloorCount(totalFloor);
      this.updateFloors();
      return;
    }

    this.floorCount = this.floorMin;
    this.updateFloors();
  }

  private normalizeFloorCount(value: number): number {
    if (!Number.isFinite(value)) {
      return this.floorMin;
    }

    const rounded = Math.trunc(value);
    return Math.min(this.floorMax, Math.max(this.floorMin, rounded));
  }
}
